import { useCallbackProxy, useEffectOnValueChange } from '@timelyapp/react';
import type { ShortcutDefinition } from 'constants/keyboard-shortcuts';
import { useEffect, useRef } from 'react';
import { notNull } from './typesafe';

/** Object containing multiple ShortcutDefinitions */
export type ShortcutMap = Record<string, ShortcutDefinition>;

/**
 * Describes a key combination.
 * A string describing the key plus boolean for modifier keys.
 *
 * @see shortcutSignatureFromString
 * @see shortcutSignatureFromEvent
 */
interface ShortcutSignature {
  key: string;
  cmd: boolean;
  ctrl: boolean;
  shift: boolean;
  alt: boolean;
  fireFromInputs?: boolean;
}

/**
 * The callback for a fired hotkey
 * @param shortcutMapKey - the shortcut that was pressed, as defined in the shortcuts object
 */
export type HotkeysCallbackFn = (
  shortcutMapKey: string,
  event: KeyboardEvent,
  signature: ShortcutSignature
) => void;

/**
 * Object stored in scope lists. Signature used for shortcut matching.
 */
interface Keybind {
  signature: ShortcutSignature;
  callback: (event: KeyboardEvent, signature: ShortcutSignature) => void;
}

/**
 * Keybinds are added to the current list.
 * A scope creates a new "current list",
 * making the previous list inactive until the scope is removed again.
 * While the new scope is active, new keybindings are added to that new list.
 * When dispatching events, only the newest (or "topmost") list is searched.
 */
const keybindScopes: Array<Array<Keybind>> = [[]];
/**
 * The special alwaysAvailable scope is always searched during dispatch,
 * so any shortcuts with `alwaysAvailable: true` will always fire, regardless of scope.
 */
const alwaysAvailableScope: Array<Keybind> = [];

let isInitialized = false;
function initialize() {
  if (isInitialized) {
    return;
  }
  // Never cleaned up, but only done once.
  document.addEventListener('keydown', dispatchEvent, false);
  isInitialized = true;
}
export function initializeHotkeysForExternalWindow(externalWindow: Window) {
  externalWindow.document.addEventListener('keydown', dispatchEvent, false);
}

function bind(shortcuts: ShortcutMap, handler: HotkeysCallbackFn): () => void {
  initialize();

  const fns = Object.keys(shortcuts).map(key => {
    const { shortcut: commaSeparatedShortcuts, alwaysAvailable }: ShortcutDefinition =
      shortcuts[key];

    const definition = shortcuts[key];
    const bound = commaSeparatedShortcuts
      .split(',')
      .map((shortcut): Keybind | null => {
        const signature = shortcutSignatureFromString(shortcut, definition);
        if (signature == null) {
          console.warn(`hotkeys: unparseable shortcut for ${key}: '${shortcut}'`);
          return null;
        }
        return {
          signature,
          callback: (event, sig) => {
            handler(key, event, sig);
          },
        };
      })
      .filter(notNull);

    const activeScope = alwaysAvailable ? alwaysAvailableScope : keybindScopes[0];
    activeScope.unshift(...bound);
    return () => {
      bound.forEach(signatureWithCallback => {
        if (activeScope.includes(signatureWithCallback)) {
          activeScope.splice(activeScope.indexOf(signatureWithCallback), 1);
        }
      });
    };
  });
  return () => {
    fns.forEach(fn => fn());
  };
}

function pushScope(): () => void {
  const introducedScope: Array<Keybind> = [];
  keybindScopes.unshift(introducedScope);

  return () => {
    if (keybindScopes.includes(introducedScope)) {
      keybindScopes.splice(keybindScopes.indexOf(introducedScope), 1);
    }
  };
}

function dispatchEvent(event: KeyboardEvent): void | false {
  const eventSignature = shortcutSignatureFromEvent(event);
  const findMatching = (haystack: Array<Keybind>) =>
    haystack.find(
      ({ signature }) =>
        eventSignature.key === signature.key &&
        eventSignature.cmd === signature.cmd &&
        eventSignature.ctrl === signature.ctrl &&
        eventSignature.alt === signature.alt &&
        // Allow shift+? to match ?
        (eventSignature.shift || !signature.shift)
    );
  const activeScope = keybindScopes[0];
  const matchingKeybind = findMatching(activeScope) || findMatching(alwaysAvailableScope);
  if (matchingKeybind != null) {
    const { cmd, ctrl, alt, key, fireFromInputs } = matchingKeybind.signature;
    const hasModifier = [cmd, ctrl, alt].some(modifier => modifier === true);
    const originatesFromInput = eventOriginatesFromInput(event);
    const isEsc = key === 'escape';
    // Make sure that single-key shortcuts, like ?, don't work when typing
    if (hasModifier || isEsc || !originatesFromInput) {
      matchingKeybind.callback(event, matchingKeybind.signature);
      event.preventDefault();
      return false;
      // In some cases, we want shortcuts to fire even on text/input fields.
    } else if (fireFromInputs && originatesFromInput) {
      matchingKeybind.callback(event, matchingKeybind.signature);
      return false;
    }
  }
  return undefined;
}

export function useHotkey(shortcuts: ShortcutMap, handler: HotkeysCallbackFn) {
  useEffectOnValueChange(shortcuts, () => {
    // We'd have to rebind, better to forbid, warn and ignore
    console.warn(`useHotkey: shortcut keymap changed. Change ignored.`);
  });

  const staticCallback = useCallbackProxy(handler);
  // We don't care if shortcuts changes - we don't support that right now
  // The reason is that scopes could rebind out of order.
  // We might switch to sending a ref or callback containing the shortcuts later, to allow hot-switching them
  useEffect(() => {
    const unbind = bind(shortcuts, staticCallback);
    return unbind;
  });
}

export function Hotkeys(props: {
  shortcuts: ShortcutMap;
  handler: HotkeysCallbackFn;
  introduceScope?: boolean;
}) {
  const { shortcuts, handler, introduceScope } = props;
  const { current: initialIntroduceScope } = useRef(introduceScope);
  useEffect(() => {
    let cleanup;
    if (initialIntroduceScope) {
      cleanup = pushScope();
    }

    return cleanup;
  }, [initialIntroduceScope]);

  useHotkey(shortcuts, handler);

  return null;
}
function shortcutSignatureFromEvent(event: KeyboardEvent): ShortcutSignature {
  // event.key can apparently be undefined sometimes. Ref:
  // https://sentry.io/organizations/timely-as/issues/1193361080/events/82c5f870267144fda7b47606f52a838b/?project=114853&query=is%3Aunresolved&sort=freq&statsPeriod=14d&utc=true
  const key = (event.key ?? '').toLowerCase();
  const { metaKey: cmd, ctrlKey: ctrl, shiftKey: shift, altKey: alt } = event;
  return {
    key,
    cmd,
    ctrl,
    shift,
    alt,
  };
}
function shortcutSignatureFromString(
  shortcut: string,
  definition?: ShortcutDefinition
): ShortcutSignature | null {
  const components = shortcut
    .toLowerCase()
    .replace(/[ \t]+/g, '')
    .split('+');
  const alt = components.find(component => ['alt', 'mod', 'modifier'].includes(component)) != null;
  const cmd = components.find(component => ['cmd', 'command', 'meta'].includes(component)) != null;
  const ctrl = components.find(component => ['ctrl', 'control'].includes(component)) != null;
  const shift = components.includes('shift');
  const key = components[components.length - 1];
  const fireFromInputs = !!definition?.fireFromInputs;
  if (key == null || key.length < 1) {
    return null;
  }
  return { key, alt, cmd, ctrl, shift, fireFromInputs };
}

function eventOriginatesFromInput(event: Event): boolean {
  if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
    return true;
  }
  return false;
}
